import { useEffect, useMemo, useState } from 'react'
import { buildPaginationTokens } from '../../../../utils/paginationTokens'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  approveMasterRequest,
  bulkApproveMasterRequests,
  bulkRejectMasterRequests,
  getMasterApprovalDetail,
  listMasterApprovals,
  listSupportedModules,
  rejectMasterRequest,
} from '../api/masterApprovalApi'
import { useMultiSelect } from '../../../../hooks/useMultiSelect'
import { useAuth } from '../../../../hooks/useAuth'
import { useApprovalSearchParams } from '../../hooks/useApprovalSearchParams'

const DEFAULT_PAGE_SIZE = 10

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function useMasterApprovalScreen() {
  const { searchParams, updateParam, setPageParam } = useApprovalSearchParams()
  const { hasAnyPrivilege } = useAuth()
  const canDecideMasters = hasAnyPrivilege(['ApprovalMastersApprove', 'CompanyApprove'])
  const canEditMasters = hasAnyPrivilege(['ApprovalMastersEdit', 'CompanyEdit'])

  const page = Number(searchParams.get('page') || 1)
  const searchText = searchParams.get('search') || ''
  const statusFilter = searchParams.get('status') || 'PENDING'
  const moduleFilter = searchParams.get('module') || 'all'
  const actionFilter = searchParams.get('action') || 'all'
  const fromDate = searchParams.get('fromDate') || ''
  const toDate = searchParams.get('toDate') || ''

  const [pageSize] = useState(DEFAULT_PAGE_SIZE)
  const [rows, setRows] = useState([])
  const [modules, setModules] = useState([])
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [totals, setTotals] = useState({ pending: 0, approved: 0, rejected: 0, archived: 0 })
  const [actionError, setActionError] = useState('')
  const [actionMessage, setActionMessage] = useState('')
  const [activeActionId, setActiveActionId] = useState(null)
  const [refreshTick, setRefreshTick] = useState(0)

  // Inspection diff modal state
  const [selectedDetail, setSelectedDetail] = useState(null)
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')

  const { isMultiSelectMode, selectedIds, toggleMultiSelectMode, toggleSelection, selectAll, clearSelection } =
    useMultiSelect()

  // Load modules list once
  useEffect(() => {
    let active = true
    async function loadModules() {
      try {
        const list = await listSupportedModules()
        if (active && Array.isArray(list)) {
          setModules(list)
        }
      } catch (err) {
        // non-blocking
      }
    }
    loadModules()
    return () => {
      active = false
    }
  }, [])

  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
      include_total: true,
      search: searchText.trim(),
    }
    if (statusFilter !== 'all') params.status = statusFilter
    if (moduleFilter !== 'all') params.module_key = moduleFilter
    if (actionFilter !== 'all') params.action = actionFilter
    if (fromDate) params.from_date = fromDate
    if (toDate) params.to_date = toDate
    return params
  }, [page, pageSize, searchText, statusFilter, moduleFilter, actionFilter, fromDate, toDate])

  useEffect(() => {
    let active = true
    async function loadRows() {
      setIsLoading(true)
      setError('')
      try {
        const response = await listMasterApprovals(queryParams)
        if (!active) return
        const items = Array.isArray(response?.items) ? response.items : []
        setRows(items)
        setMeta({
          total: Number(response?.total_count || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.page_size || pageSize),
          hasMore: Boolean(response?.has_next),
        })
        setTotals(response?.approval_summary || { pending: 0, approved: 0, rejected: 0, archived: 0 })
      } catch (requestError) {
        if (!active) return
        setRows([])
        setError(extractApiErrorMessage(requestError, 'Failed to load master approval requests.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadRows()
    return () => {
      active = false
    }
  }, [page, pageSize, queryParams, refreshTick])

  const mappedRows = useMemo(() => {
    return rows.map((row) => ({
      id: Number(row.id),
      moduleKey: row.module_key,
      moduleName: row.module_name,
      action: row.action,
      entityId: row.entity_id,
      entityName: row.entity_name,
      requestedBy: row.requested_by_name || 'User',
      requestedAt: formatDate(row.requested_at),
      approvalStatus: String(row.status || '').toUpperCase(),
      revision: row.revision,
      canApprove: Boolean(row.can_approve),
      canEdit: Boolean(row.can_edit),
      entryRoute: row.entry_route,
    }))
  }, [rows])

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!mappedRows.length) return 0
    return (page - 1) * pageSize + 1
  }, [mappedRows.length, page, pageSize])

  const rangeEnd = useMemo(() => {
    if (!mappedRows.length) return 0
    return rangeStart + mappedRows.length - 1
  }, [mappedRows.length, rangeStart])

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setPageParam(nextPage)
  }

  function goToPreviousPage() {
    goToPage(Math.max(1, page - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) goToPage(page + 1)
  }

  async function openDetailModal(requestId) {
    setIsDetailLoading(true)
    setDetailError('')
    try {
      const data = await getMasterApprovalDetail(requestId)
      setSelectedDetail(data)
    } catch (err) {
      setDetailError(extractApiErrorMessage(err, 'Failed to load request details.'))
      setSelectedDetail({ id: requestId })
    } finally {
      setIsDetailLoading(false)
    }
  }

  function closeDetailModal() {
    setSelectedDetail(null)
    setDetailError('')
  }

  async function runDecision(requestId, actionType, reason) {
    setActionError('')
    setActionMessage('')
    setActiveActionId(requestId)
    try {
      if (actionType === 'approve') {
        await approveMasterRequest(requestId, reason)
        setActionMessage('Request approved and changes applied successfully.')
      } else {
        await rejectMasterRequest(requestId, reason)
        setActionMessage('Request rejected successfully.')
      }
      closeDetailModal()
      setRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setActionError(extractApiErrorMessage(requestError, 'Decision execution failed.'))
    } finally {
      setActiveActionId(null)
    }
  }

  async function runBulkDecision(actionType) {
    if (selectedIds.size === 0) return
    const ids = Array.from(selectedIds)
    setActionError('')
    setActionMessage('')
    try {
      if (actionType === 'approve') {
        await bulkApproveMasterRequests({ ids })
        setActionMessage(`Successfully approved ${ids.length} request(s).`)
      } else {
        await bulkRejectMasterRequests({ ids })
        setActionMessage(`Successfully rejected ${ids.length} request(s).`)
      }
      clearSelection()
      setRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setActionError(extractApiErrorMessage(requestError, 'Bulk decision action failed.'))
    }
  }

  return {
    state: {
      isLoading,
      error,
      actionError,
      actionMessage,
      activeActionId,
      rows: mappedRows,
      modules,
      page,
      totalPages,
      pageTokens: buildPaginationTokens(page, totalPages),
      totalCount: meta.total,
      rangeStart,
      rangeEnd,
      searchText,
      statusFilter,
      moduleFilter,
      actionFilter,
      fromDate,
      toDate,
      totals,
      isMultiSelectMode,
      selectedIds,
      canDecideMasters,
      canEditMasters,
      selectedDetail,
      isDetailLoading,
      detailError,
    },
    actions: {
      setSearchText: (value) => updateParam('search', value),
      setStatusFilter: (value) => updateParam('status', value),
      setModuleFilter: (value) => updateParam('module', value),
      setActionFilter: (value) => updateParam('action', value),
      setFromDate: (value) => updateParam('fromDate', value),
      setToDate: (value) => updateParam('toDate', value),
      goToPreviousPage,
      goToNextPage,
      goToPage,
      openDetailModal,
      closeDetailModal,
      approveRequest: (requestId, reason) => runDecision(requestId, 'approve', reason),
      rejectRequest: (requestId, reason) => runDecision(requestId, 'reject', reason),
      toggleMultiSelectMode,
      toggleSelection,
      selectAll: () => {
        const selectable = mappedRows.filter((r) => r.approvalStatus === 'PENDING' && r.canApprove).map((r) => r.id)
        selectAll(selectable)
      },
      bulkApprove: () => runBulkDecision('approve'),
      bulkReject: () => runBulkDecision('reject'),
    },
  }
}
